// Reads a server-sent-event stream from a POST endpoint.
//
// In the native app, CapacitorHttp replaces window.fetch with a native call
// that only returns once the whole response has arrived, so streaming uses the
// webview's own fetch, which Capacitor keeps as window.CapacitorWebFetch.

const webFetch = () =>
  (typeof window !== "undefined" && (window.CapacitorWebFetch || window.fetch)).bind(window);

/**
 * POSTs `body` and calls onDelta(text) for each streamed piece and onReset()
 * if the server restarts the answer. Resolves with the final `done` payload.
 * Throws if the stream can't be opened or ends without a result, so callers
 * can fall back to the regular request.
 */
export async function postEventStream(url, body, { onDelta, onReset, signal } = {}) {
  const res = await webFetch()(url, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "text/event-stream" },
    body: JSON.stringify(body),
    credentials: "omit",
    signal,
  });
  if (!res.ok || !res.body) throw new Error(`stream unavailable (${res.status})`);

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let sep;
    while ((sep = buffer.indexOf("\n\n")) !== -1) {
      const raw = buffer.slice(0, sep);
      buffer = buffer.slice(sep + 2);
      let event = "message";
      const data = [];
      for (const line of raw.split("\n")) {
        if (line.startsWith("event:")) event = line.slice(6).trim();
        else if (line.startsWith("data:")) data.push(line.slice(5).replace(/^ /, ""));
      }
      if (!data.length) continue; // heartbeat
      const payload = JSON.parse(data.join("\n"));
      if (event === "delta") onDelta?.(payload);
      else if (event === "reset") onReset?.();
      else if (event === "done") return payload;
    }
  }
  throw new Error("stream ended early");
}
